<template>
  <pwa-layout title="Véhicules" subtitle="GESTION DE FLOTTE" :show-back="true">
    <template #header-action>
      <button class="pwa-icon-btn" @click="showFilters = !showFilters">
        <span class="mdi mdi-tune" style="font-size:22px;color:var(--pwa-text)"></span>
      </button>
    </template>

    <div class="pwa-vehicles">

      <!-- ── Partie fixe : recherche + stats + titre ── -->
      <div class="pwa-fixed-top">
        <div class="pwa-search-bar">
          <span class="mdi mdi-magnify pwa-search-icon"></span>
          <input v-model="search" class="pwa-search-input" placeholder="Plaque, chauffeur..." />
        </div>

        <div class="pwa-stats-row">
          <div class="pwa-stat-card pwa-stat-card--dark">
            <div class="pwa-stat-row">
              <div>
                <div class="pwa-stat-label">Total Flotte</div>
                <div class="pwa-stat-value">{{ statsData.total }}</div>
                <div class="pwa-stat-sub">véhicules</div>
              </div>
              <span class="mdi mdi-truck-fast-outline pwa-stat-icon"></span>
            </div>
          </div>
          <div class="pwa-stat-card">
            <div class="pwa-stat-row">
              <div>
                <div class="pwa-stat-label" style="color:var(--pwa-error)">Bloqués</div>
                <div class="pwa-stat-value" style="color:var(--pwa-error)">{{ statsData.blocked }}</div>
                <div class="pwa-stat-sub">véhicules</div>
              </div>
              <span class="mdi mdi-car-off pwa-stat-icon" style="color:var(--pwa-error)"></span>
            </div>
          </div>
        </div>

      </div>

      <!-- ── Liste scrollable ── -->
      <div ref="listEl" class="pwa-veh-list" @scroll="onScroll">
        <div v-if="loading && !vehicles.length" class="pwa-loading">
          <span class="mdi mdi-loading mdi-spin"></span>
        </div>
        <template v-else>
          <div v-for="v in filtered" :key="v.id" class="pwa-vehicle-card" @click="$router.push('/pwa/vehicles/' + v.id)">
            <div class="pwa-vehicle-icon" :class="vehicleIconClass(v)">
              <span class="mdi" :class="vehicleIcon(v)"></span>
            </div>
            <div class="pwa-vehicle-info">
              <div class="pwa-vehicle-header">
                <span class="pwa-vehicle-reg">{{ v.registration_number }}</span>
                <span class="pwa-vehicle-status-chip" :class="statusChipClass(v)">{{ statusLabel(v) }}</span>
              </div>
              <div class="pwa-vehicle-model">{{ v.make || '' }}{{ v.model ? ' ' + v.model : '' }}</div>
              <div class="pwa-vehicle-meta">
                <span class="pwa-meta-item">
                  <span class="mdi mdi-account-outline"></span>
                  {{ driverName(v) }}
                </span>
                <span class="pwa-meta-item">
                  <span class="mdi mdi-calendar"></span>
                  {{ v.year || '—' }}
                </span>
              </div>
            </div>
            <span class="mdi mdi-chevron-right pwa-vehicle-chevron"></span>
          </div>

          <!-- Loader bas de page -->
          <div v-if="loadingMore" class="pwa-load-more">
            <span class="mdi mdi-loading mdi-spin"></span>
          </div>

          <div v-if="!loading && !filtered.length" class="pwa-empty">Aucun véhicule trouvé</div>
        </template>
      </div>

    </div>

    <!-- FAB + -->
    <template #fab>
      <button class="pwa-fab" @click="$router.push('/pwa/vehicles/new')">
        <span class="mdi mdi-plus"></span>
      </button>
    </template>
  </pwa-layout>
</template>

<script>
import PwaLayout from './PwaLayout.vue'
import CompanyVehiclesService from '@/services/CompanyVehiclesService'

const PAGE_SIZE = 15

export default {
  name: 'PwaVehicles',
  components: { PwaLayout },
  data() {
    return {
      vehicles: [],
      statsData: { total: 0, blocked: 0 },
      loading: false,
      loadingMore: false,
      page: 1,
      hasMore: true,
      search: '',
      showFilters: false,
    }
  },
  computed: {
    department() {
      return this.$store.state.app.project
    },
    filtered() {
      if (!this.search) return this.vehicles
      const q = this.search.toLowerCase()
      return this.vehicles.filter(v =>
        (v.registration_number || '').toLowerCase().includes(q) ||
        (v.driver_first_name || '').toLowerCase().includes(q) ||
        (v.driver_last_name || '').toLowerCase().includes(q)
      )
    },
  },
  mounted() {
    this.loadVehicles()
  },
  methods: {
    async loadVehicles() {
      this.loading = true
      this.page = 1
      this.hasMore = true
      this.vehicles = []
      try {
        const params = { page: 1, page_size: PAGE_SIZE }
        if (this.department?.id) params.department = this.department.id
        const data = await CompanyVehiclesService.get(this, null, params)
        const results = Array.isArray(data) ? data : (data?.results || [])
        this.vehicles = results
        const s = data?.stats || {}
        this.statsData = {
          total: s.total ?? data?.count ?? results.length,
          blocked: s.blocked ?? results.filter(v => v.is_blocked).length,
        }
        this.hasMore = results.length === PAGE_SIZE
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async loadMore() {
      if (this.loadingMore || !this.hasMore) return
      this.loadingMore = true
      this.page++
      try {
        const params = { page: this.page, page_size: PAGE_SIZE }
        if (this.department?.id) params.department = this.department.id
        const data = await CompanyVehiclesService.get(this, null, params)
        const results = Array.isArray(data) ? data : (data?.results || [])
        this.vehicles.push(...results)
        this.hasMore = results.length === PAGE_SIZE
      } catch (e) {
        console.error(e)
        this.page--
      } finally {
        this.loadingMore = false
      }
    },
    onScroll() {
      const el = this.$refs.listEl
      if (!el) return
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80
      if (nearBottom) this.loadMore()
    },
    driverName(v) {
      return `${v.driver_first_name || ''} ${v.driver_last_name || ''}`.trim() || 'Non assigné'
    },
    statusLabel(v) {
      if (v.is_blocked) return 'BLOQUÉ'
      if (v.status === 'garage') return 'GARAGE'
      return 'EN SERVICE'
    },
    statusChipClass(v) {
      if (v.is_blocked) return 'pwa-chip-blocked'
      if (v.status === 'garage') return 'pwa-chip-garage'
      return 'pwa-chip-active'
    },
    vehicleIcon(v) {
      if (v.is_blocked) return 'mdi-car-off'
      return 'mdi-car'
    },
    vehicleIconClass(v) {
      if (v.is_blocked) return 'pwa-vi--red'
      if (v.status === 'garage') return 'pwa-vi--grey'
      return 'pwa-vi--blue'
    },
  },
}
</script>

<style scoped>
.pwa-vehicles {
  display: flex;
  flex-direction: column;
  height: calc(100dvh - var(--pwa-header-h) - var(--pwa-nav-h) - 8px);
  overflow: hidden;
}

/* Partie fixe */
.pwa-fixed-top {
  flex-shrink: 0;
  padding: 16px 16px 0;
  background: var(--pwa-bg);
}

/* Liste scrollable */
.pwa-veh-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
  min-height: 0;
}

.pwa-search-bar {
  display: flex; align-items: center; background: var(--pwa-white);
  border-radius: 12px; padding: 0 12px; height: 46px; gap: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06); margin-bottom: 14px;
}
.pwa-search-icon { font-size: 18px; color: var(--pwa-muted); }
.pwa-search-input { flex: 1; border: none; outline: none; font-size: 14px; color: var(--pwa-text); background: transparent; }
.pwa-search-input::placeholder { color: var(--pwa-muted); }

.pwa-stats-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
.pwa-stat-card { background: var(--pwa-white); border-radius: var(--pwa-card-radius); padding: 16px; }
.pwa-stat-card--dark { background: var(--pwa-dark); }
.pwa-stat-card--dark .pwa-stat-label,
.pwa-stat-card--dark .pwa-stat-value,
.pwa-stat-card--dark .pwa-stat-sub,
.pwa-stat-card--dark .pwa-stat-icon { color: white !important; }
.pwa-stat-row { display: flex; justify-content: space-between; align-items: flex-start; }
.pwa-stat-label { font-size: 11px; font-weight: 600; color: var(--pwa-muted); letter-spacing: 0.5px; margin-bottom: 4px; }
.pwa-stat-value { font-size: 28px; font-weight: 700; color: var(--pwa-text); line-height: 1; }
.pwa-stat-sub { font-size: 12px; color: var(--pwa-muted); margin-top: 2px; }
.pwa-stat-icon { font-size: 28px; opacity: 0.3; }

.pwa-list-title-row { display: flex; align-items: center; gap: 8px; padding-bottom: 10px; }
.pwa-list-heading { font-size: 16px; font-weight: 700; color: var(--pwa-text); }
.pwa-list-count {
  font-size: 12px; font-weight: 600; background: var(--pwa-accent);
  color: white; border-radius: 10px; padding: 1px 8px;
}

/* Vehicle card */
.pwa-vehicle-card {
  background: var(--pwa-white); border-radius: var(--pwa-card-radius);
  padding: 14px 12px 14px 14px; margin-bottom: 10px;
  display: flex; align-items: center; gap: 12px;
  cursor: pointer; transition: box-shadow 0.2s;
}
.pwa-vehicle-card:active { box-shadow: 0 2px 12px rgba(0,0,0,0.1); }
.pwa-vehicle-icon {
  width: 48px; height: 48px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; flex-shrink: 0;
}
.pwa-vi--blue { background: #E3F2FD; color: #1976D2; }
.pwa-vi--red  { background: #FFEBEE; color: #C62828; }
.pwa-vi--grey { background: #F0F4F8; color: var(--pwa-muted); }
.pwa-vehicle-info { flex: 1; min-width: 0; }
.pwa-vehicle-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px; }
.pwa-vehicle-reg { font-size: 16px; font-weight: 700; color: var(--pwa-text); }
.pwa-vehicle-model { font-size: 13px; color: var(--pwa-muted); margin-bottom: 6px; }
.pwa-vehicle-meta { display: flex; gap: 14px; flex-wrap: wrap; }
.pwa-meta-item { display: flex; align-items: center; gap: 4px; font-size: 12px; color: var(--pwa-muted); }
.pwa-meta-item .mdi { font-size: 14px; }
.pwa-vehicle-chevron { font-size: 20px; color: #D1D9E6; flex-shrink: 0; }

/* Status chips */
.pwa-vehicle-status-chip { font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 6px; letter-spacing: 0.5px; }
.pwa-chip-active  { background: #E8F5E9; color: #2E7D32; border: 1px solid #A5D6A7; }
.pwa-chip-blocked { background: #FFEBEE; color: #C62828; border: 1px solid #FFCDD2; }
.pwa-chip-garage  { background: #F0F4F8; color: var(--pwa-muted); border: 1px solid #D1D9E6; }

.pwa-loading { text-align: center; padding: 32px; font-size: 24px; color: var(--pwa-muted); }
.pwa-load-more { text-align: center; padding: 16px; font-size: 22px; color: var(--pwa-muted); }
.pwa-empty { text-align: center; padding: 32px; color: var(--pwa-muted); font-size: 14px; }

.pwa-icon-btn {
  width: 40px; height: 40px; border: none; background: transparent;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
}

.pwa-fab {
  position: absolute;
  bottom: calc(var(--pwa-nav-h) + 16px);
  right: 20px;
  width: 52px; height: 52px;
  border-radius: 16px;
  background: var(--pwa-dark);
  color: white;
  border: none;
  font-size: 28px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  cursor: pointer;
  z-index: 50;
}
</style>
